/**
 * R3 落地：自定义 Logger
 *
 * 功能：
 * 1. 字段黑名单过滤（amount/paid/qr/qrcode/sig/nonce/ticket/secret/token/phone/idcard/password）
 * 2. 生产环境静默 info/debug（由 vite build.esbuild.drop 在编译期删除 console 调用）
 * 3. 统一日志格式：[CardShop][level][timestamp] message {redacted_data}
 * 4. error 级别始终输出（不被屏蔽）
 */

/** 敏感字段黑名单（不区分大小写） */
const REDACTED_FIELDS = new Set([
  'amount',
  'paid',
  'qr',
  'qrcode',
  'sig',
  'nonce',
  'ticket',
  'secret',
  'token',
  'phone',
  'idcard',
  'id_card',
  'password',
  'passwd',
  'credential',
  'hmac',
])

/** 替换占位符 */
const REDACT_PLACEHOLDER = '[REDACTED]'

/** 递归脱敏对象 */
function redact(value: unknown, depth = 0): unknown {
  if (depth > 10) return value // 防止循环引用死循环
  if (value === null || value === undefined) return value
  if (typeof value !== 'object') return value

  if (Array.isArray(value)) {
    return value.map((item) => redact(item, depth + 1))
  }

  const result: Record<string, unknown> = {}
  for (const [key, val] of Object.entries(value as Record<string, unknown>)) {
    if (REDACTED_FIELDS.has(key.toLowerCase())) {
      result[key] = REDACT_PLACEHOLDER
    } else {
      result[key] = redact(val, depth + 1)
    }
  }
  return result
}

/** 格式化单条日志数据 */
function formatData(data: unknown): string {
  if (data === undefined) return ''
  try {
    return JSON.stringify(redact(data))
  } catch {
    return String(data)
  }
}

/** 构造日志前缀 */
function prefix(level: string): string {
  const ts = new Date().toISOString()
  return `[CardShop][${level}][${ts}]`
}

/** 运行时判断是否为开发环境（每次调用时读取，方便单测 mock） */
function isDevMode(): boolean {
  try {
    return typeof __DEV__ !== 'undefined' ? __DEV__ : true
  } catch {
    return true
  }
}

export const logger = {
  /**
   * info 级别（生产环境被 vite drop_console 编译删除）
   */
  info(message: string, data?: unknown): void {
    if (!isDevMode()) return
    console.warn(`${prefix('INFO')} ${message}`, data !== undefined ? formatData(data) : '')
  },

  /**
   * warn 级别（始终输出，含脱敏）
   */
  warn(message: string, data?: unknown): void {
    console.warn(`${prefix('WARN')} ${message}`, data !== undefined ? formatData(data) : '')
  },

  /**
   * error 级别（始终输出，不被屏蔽）
   */
  error(message: string, data?: unknown): void {
    console.error(`${prefix('ERROR')} ${message}`, data !== undefined ? formatData(data) : '')
  },

  /**
   * debug 级别（仅 dev，生产编译期删除）
   */
  debug(message: string, data?: unknown): void {
    if (!isDevMode()) return
    console.warn(`${prefix('DEBUG')} ${message}`, data !== undefined ? formatData(data) : '')
  },
}

/** 导出 redact 工具函数（供单测验证） */
export { redact, REDACTED_FIELDS }
